import React from 'react';

import { Tracking } from './../analytics/tracking';

import { debounce } from 'lodash';

import { useAuthenticate } from './useAuthenticate';
import { hasAssignedLocations } from '../utils/funcs';

import { genericMerchantDB, remoteDB } from '../integrations/pouch/DBConn';
import { getSessionInfo } from '../utils/getSessionInfo';
import { handleCouchError } from '../utils/handleCouchError';
import { EVENTS } from '../utils/events';
import { captureException } from '../integrations/sentry/capture';
interface IReplicator {
	localDBName: string;
	handleReplicationStatus: (v: boolean) => void;
	updateOnChange: () => Promise<void>;
}

const getDebouncedTrackChange = (dbName: string) =>
	debounce((docsLength: number) => {
		Tracking.capture(EVENTS.REPLICATION.CHANGE(dbName), {
			direction: 'push',
			docCount: docsLength,
			timestamp: Date.now(),
		});
	}, 10000);

export const useReplicatorTo = ({ localDBName, handleReplicationStatus }: IReplicator) => {
	const [isReplicating, setIsReplicating] = React.useState(false);

	const replicateRef = React.useRef<any>(null);

	const liveReplicationRef = React.useRef<any>(null);

	const isAuthenticated = useAuthenticate();

	const debouncedTrackChange = getDebouncedTrackChange(localDBName);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(() => {
		if (!isAuthenticated) {
			return;
		}

		const { userInfo } = getSessionInfo();

		if (!hasAssignedLocations(userInfo)) {
			return;
		}

		handleReplicationStatus(true);
		setIsReplicating(true);

		Tracking.capture(EVENTS.REPLICATION.STARTED(localDBName), {
			userId: userInfo?._id,
			dbName: localDBName,
			direction: 'to',
			startedAt: new Date().toISOString(),
		});

		replicateRef.current = genericMerchantDB(localDBName)
			.replicate.to(remoteDB(userInfo?.merchantName), {})
			.on('complete', (info) => {
				if (info.status === 'complete') {
					handleReplicationStatus(false);
					setIsReplicating(false);

					liveReplicationRef.current = genericMerchantDB(localDBName)
						.replicate.to(remoteDB(userInfo?.merchantName), { live: true, retry: true })
						.on('error', (err) => {
							Tracking.capture(EVENTS.REPLICATION.FAILED(localDBName), {
								userId: userInfo?._id,
								dbName: localDBName,
								direction: 'to',
								status: 'error',
								error: err,
								type: 'live',
							});
							captureException(err, { fn: 'useReplicatorTo.liveReplication.error', args: { localDBName } });
						})
						.on('change', async (info: any) => {
							debouncedTrackChange(info.docs?.length ?? 0);
						});
				}
			})
			.on('error', (error) => {
				Tracking.capture(EVENTS.REPLICATION.FAILED(localDBName), {
					userId: userInfo?._id,
					dbName: localDBName,
					direction: 'to',
					status: 'error',
					error: error,
					type: 'one-time',
				});
				handleCouchError(error);
				captureException(error, { fn: 'useReplicatorTo.oneTimeReplication.error', args: { localDBName } });
			})
			.on('change', (info) => {
				debouncedTrackChange(info.docs?.length ?? 0);
			})
			.on('denied', (info: any) => {
				captureException(new Error('replication_denied'), {
					fn: 'useReplicatorTo.replication.denied',
					args: { localDBName, info },
				});
			});

		return () => {
			replicateRef.current && replicateRef.current?.cancel();

			liveReplicationRef.current && liveReplicationRef?.current.cancel();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { isReplicating };
};
